import { useCallback, useEffect, useRef, useState } from "react";

const spacingFor = () => (window.innerWidth >= 640 ? 215 : 150);

export const useRailPhysics = (count) => {
  const [pos, setPos] = useState(0);
  const [vel, setVel] = useState(0);
  const [spacing, setSpacing] = useState(spacingFor);
  const trackRef = useRef(null);
  const s = useRef({ pos: 0, vel: 0, target: null, drag: null, raf: 0, wheelT: 0, suppress: false });

  const max = Math.max(0, count - 1);
  const clamp = useCallback((v) => Math.max(0, Math.min(max, v)), [max]);
  const soft = useCallback((v) => (v < 0 ? v * 0.3 : v > max ? max + (v - max) * 0.3 : v), [max]);
  const render = () => { setPos(s.current.pos); setVel(s.current.vel); };

  const loop = useCallback(() => {
    const st = s.current;
    if (!st.drag && st.target != null) {
      const diff = st.target - st.pos;
      st.pos += diff * 0.14;
      st.vel *= 0.86;
      if (Math.abs(diff) < 0.0015 && Math.abs(st.vel) < 0.002) {
        st.pos = st.target; st.target = null; st.vel = 0; st.raf = 0;
        render();
        return;
      }
    }
    render();
    st.raf = requestAnimationFrame(loop);
  }, []);

  const kick = useCallback(() => { if (!s.current.raf) s.current.raf = requestAnimationFrame(loop); }, [loop]);
  const goTo = useCallback((i) => { s.current.target = clamp(Math.round(i)); kick(); }, [clamp, kick]);

  useEffect(() => {
    const st = s.current;
    const onResize = () => setSpacing(spacingFor());
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); cancelAnimationFrame(st.raf); st.raf = 0; };
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const st = s.current;
      const horiz = Math.abs(e.deltaX) > Math.abs(e.deltaY);
      if (!horiz) return;
      const delta = e.deltaX;
      const atEdge = (delta < 0 && st.pos <= 0.01) || (delta > 0 && st.pos >= max - 0.01);
      if (atEdge) return;
      e.preventDefault();
      st.target = null;
      st.pos = soft(st.pos + delta / spacing);
      st.vel = 0.7 * st.vel + 0.3 * (delta / spacing);
      kick();
      clearTimeout(st.wheelT);
      st.wheelT = setTimeout(() => goTo(st.pos + st.vel * 3), 130);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [max, spacing, soft, kick, goTo, count]);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const st = s.current;
    st.drag = { x: e.clientX, y: e.clientY, start: st.pos, last: st.pos, t: performance.now(), axis: null, id: e.pointerId };
    st.suppress = false;
  };

  const onPointerMove = (e) => {
    const st = s.current;
    const d = st.drag;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.axis) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (d.axis === "y") { st.drag = null; if (e.pointerType === "mouse") st.suppress = true; return; }
      e.currentTarget.setPointerCapture?.(e.pointerId);
      st.target = null;
      st.suppress = true;
      kick();
    }
    const now = performance.now();
    const next = soft(d.start - dx / spacing);
    const inst = ((next - d.last) / Math.max(1, now - d.t)) * 16;
    st.vel = 0.75 * st.vel + 0.25 * inst;
    st.pos = next;
    d.last = next;
    d.t = now;
  };

  const onPointerUp = () => {
    const st = s.current;
    if (!st.drag) return;
    const dragged = st.drag.axis === "x";
    st.drag = null;
    if (dragged) goTo(st.pos + st.vel * 14);
  };

  const onClickCapture = (e) => {
    if (s.current.suppress) { e.preventDefault(); e.stopPropagation(); s.current.suppress = false; }
  };

  return {
    pos, vel, spacing, trackRef, goTo,
    active: Math.round(clamp(pos)),
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onClickCapture },
  };
};
